// ===========================================
// Custom SMS & WhatsApp Management Page
// ===========================================
// Admin interface for managing SMS/WhatsApp campaigns

import { useState, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTenant } from '../../contexts/TenantContext';
import {
  Box,
  TextField,
  Alert,
  Chip,
  Button,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Menu,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ContentCopy as DuplicateIcon,
  Send as SendIcon,
  Schedule as ScheduleIcon,
  MoreVert as MoreIcon,
  Analytics as AnalyticsIcon,
  Cancel as CancelIcon,
  Sms as SmsIcon,
  WhatsApp as WhatsAppIcon,
} from '@mui/icons-material';
import PageHeader from '../../components/common/PageHeader';
import DataTable, { Column } from '../../components/common/DataTable';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import {
  useCustomSmsCampaigns,
  useDeleteCustomSmsCampaign,
  useDuplicateCustomSmsCampaign,
  useSendCustomSmsCampaign,
  useCancelScheduledSmsCampaign,
} from '../../hooks/useCustomSms';
import {
  CustomSmsCampaignListItem,
  CustomSmsStatus,
  CustomSmsChannel,
  CustomSmsCampaignFilters,
} from '../../types/customSms.types';

// ===========================================
// STATUS & CHANNEL DISPLAY
// ===========================================

const statusColors: Record<
  CustomSmsStatus,
  'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'
> = {
  DRAFT: 'default',
  SCHEDULED: 'info',
  SENDING: 'warning',
  SENT: 'success',
  FAILED: 'error',
};

const statusLabels: Record<CustomSmsStatus, string> = {
  DRAFT: 'Draft',
  SCHEDULED: 'Scheduled',
  SENDING: 'Sending',
  SENT: 'Sent',
  FAILED: 'Failed',
};

function ChannelChip({ channel }: { channel: CustomSmsChannel }) {
  if (channel === 'SMS') {
    return <Chip icon={<SmsIcon />} label="SMS" size="small" color="primary" variant="outlined" />;
  }
  if (channel === 'WHATSAPP') {
    return (
      <Chip icon={<WhatsAppIcon />} label="WhatsApp" size="small" color="success" variant="outlined" />
    );
  }
  return (
    <Stack direction="row" spacing={0.5}>
      <Chip icon={<SmsIcon />} label="SMS" size="small" variant="outlined" />
      <Chip icon={<WhatsAppIcon />} label="WA" size="small" color="success" variant="outlined" />
    </Stack>
  );
}

// ===========================================
// COMPONENT
// ===========================================

export default function CustomSmsPage() {
  const navigate = useNavigate();
  const { schoolSlug } = useTenant();

  const [filters, setFilters] = useState<CustomSmsCampaignFilters>({
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });
  const [searchTerm, setSearchTerm] = useState('');

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedCampaign, setSelectedCampaign] = useState<CustomSmsCampaignListItem | null>(null);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [campaignToDelete, setCampaignToDelete] = useState<CustomSmsCampaignListItem | null>(null);

  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [campaignToSend, setCampaignToSend] = useState<CustomSmsCampaignListItem | null>(null);

  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [campaignToCancel, setCampaignToCancel] = useState<CustomSmsCampaignListItem | null>(null);

  const { data: campaignsData, isLoading, error } = useCustomSmsCampaigns({
    ...filters,
    search: searchTerm || undefined,
  });
  const deleteMutation = useDeleteCustomSmsCampaign();
  const duplicateMutation = useDuplicateCustomSmsCampaign();
  const sendMutation = useSendCustomSmsCampaign();
  const cancelMutation = useCancelScheduledSmsCampaign();

  const campaigns = useMemo(() => campaignsData?.data || [], [campaignsData]);

  const columns: Column<CustomSmsCampaignListItem>[] = [
    {
      id: 'title',
      label: 'Title',
      minWidth: 200,
      format: (value, row) => (
        <Box>
          <Typography variant="body2" fontWeight={500}>
            {value as string}
          </Typography>
          <ChannelChip channel={row.channel} />
        </Box>
      ),
    },
    {
      id: 'status',
      label: 'Status',
      minWidth: 100,
      format: (value) => (
        <Chip
          label={statusLabels[value as CustomSmsStatus]}
          size="small"
          color={statusColors[value as CustomSmsStatus]}
        />
      ),
    },
    {
      id: 'author',
      label: 'Author',
      minWidth: 130,
      format: (_, row) => `${row.author.firstName} ${row.author.lastName}`,
    },
    {
      id: 'totalRecipients',
      label: 'Recipients',
      minWidth: 100,
      align: 'right',
      format: (value) => (value as number) || '-',
    },
    {
      id: 'sentCount',
      label: 'Sent',
      minWidth: 80,
      align: 'right',
      format: (value, row) => {
        const sent = value as number;
        const total = row.totalRecipients;
        return total > 0 ? `${sent}/${total}` : '-';
      },
    },
    {
      id: 'deliveredCount',
      label: 'Delivered',
      minWidth: 90,
      align: 'right',
      format: (value, row) => {
        const delivered = value as number;
        const sent = row.sentCount;
        if (sent === 0) return '-';
        const rate = ((delivered / sent) * 100).toFixed(0);
        return `${delivered} (${rate}%)`;
      },
    },
    {
      id: 'scheduledFor',
      label: 'Scheduled',
      minWidth: 120,
      format: (value) =>
        value ? new Date(value as string).toLocaleString() : '-',
    },
    {
      id: 'createdAt',
      label: 'Created',
      minWidth: 100,
      format: (value) => new Date(value as string).toLocaleDateString(),
    },
  ];

  const handleEdit = (campaign: CustomSmsCampaignListItem) => {
    navigate(`/${schoolSlug}/admin/custom-sms/${campaign.id}`);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, campaign: CustomSmsCampaignListItem) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setSelectedCampaign(campaign);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedCampaign(null);
  };

  const handleDuplicate = async () => {
    if (!selectedCampaign) return;
    try {
      await duplicateMutation.mutateAsync(selectedCampaign.id);
    } catch (err) {
      console.error('Failed to duplicate campaign:', err);
    }
    handleMenuClose();
  };

  const handleSendClick = () => {
    if (!selectedCampaign) return;
    setCampaignToSend(selectedCampaign);
    setSendDialogOpen(true);
    handleMenuClose();
  };

  const handleSendConfirm = async () => {
    if (!campaignToSend) return;
    try {
      await sendMutation.mutateAsync(campaignToSend.id);
      setSendDialogOpen(false);
      setCampaignToSend(null);
    } catch (err) {
      console.error('Failed to send campaign:', err);
    }
  };

  const handleCancelClick = () => {
    if (!selectedCampaign) return;
    setCampaignToCancel(selectedCampaign);
    setCancelDialogOpen(true);
    handleMenuClose();
  };

  const handleCancelConfirm = async () => {
    if (!campaignToCancel) return;
    try {
      await cancelMutation.mutateAsync(campaignToCancel.id);
      setCancelDialogOpen(false);
      setCampaignToCancel(null);
    } catch (err) {
      console.error('Failed to cancel scheduled campaign:', err);
    }
  };

  const handleDeleteClick = () => {
    if (!selectedCampaign) return;
    setCampaignToDelete(selectedCampaign);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = async () => {
    if (!campaignToDelete) return;
    try {
      await deleteMutation.mutateAsync(campaignToDelete.id);
      setDeleteDialogOpen(false);
      setCampaignToDelete(null);
    } catch (err) {
      console.error('Failed to delete campaign:', err);
    }
  };

  /**
   * Extra row actions, rendered INSIDE DataTable's own actions cell.
   *
   * WARNING: Do NOT append a second column to `columns`. DataTable renders its
   * own actions column whenever onEdit/onDelete/onView/renderExtraActions is
   * supplied, so appending one produced TWO actions columns and pushed the real
   * controls out of view -- the page looked like it had no actions at all.
   */
  const renderExtraActions = (row: CustomSmsCampaignListItem) => (
    <Tooltip title="More actions">
      <IconButton size="small" onClick={(e) => handleMenuOpen(e, row)}>
        <MoreIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );

  return (
    <Box>
      <PageHeader
        title="SMS & WhatsApp Campaigns"
        subtitle="Create and send targeted SMS and WhatsApp campaigns"
        action={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            component={Link}
            to={`/${schoolSlug}/admin/custom-sms/new`}
          >
            Create Campaign
          </Button>
        }
      />

      {/* Filters */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }} alignItems="center">
        <TextField
          label="Search"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="small"
          sx={{ minWidth: 250 }}
          placeholder="Search by title..."
        />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={filters.status || ''}
            label="Status"
            onChange={(e) =>
              setFilters({ ...filters, status: (e.target.value as CustomSmsStatus) || undefined })
            }
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="DRAFT">Draft</MenuItem>
            <MenuItem value="SCHEDULED">Scheduled</MenuItem>
            <MenuItem value="SENDING">Sending</MenuItem>
            <MenuItem value="SENT">Sent</MenuItem>
            <MenuItem value="FAILED">Failed</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Channel</InputLabel>
          <Select
            value={filters.channel || ''}
            label="Channel"
            onChange={(e) =>
              setFilters({ ...filters, channel: (e.target.value as CustomSmsChannel) || undefined })
            }
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="SMS">SMS</MenuItem>
            <MenuItem value="WHATSAPP">WhatsApp</MenuItem>
            <MenuItem value="BOTH">Both</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load campaigns: {error instanceof Error ? error.message : 'Unknown error'}
        </Alert>
      )}

      <DataTable<CustomSmsCampaignListItem>
        columns={columns}
        renderExtraActions={renderExtraActions}
        data={campaigns}
        loading={isLoading}
        onEdit={handleEdit}
        emptyMessage="No campaigns found. Create your first SMS or WhatsApp campaign to get started."
      />

      {/* Context Menu */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleMenuClose}>
        <MenuItem onClick={handleDuplicate}>
          <DuplicateIcon fontSize="small" sx={{ mr: 1 }} /> Duplicate
        </MenuItem>
        {selectedCampaign?.status === 'DRAFT' && (
          <MenuItem onClick={handleSendClick}>
            <SendIcon fontSize="small" sx={{ mr: 1 }} /> Send Now
          </MenuItem>
        )}
        {selectedCampaign?.status === 'DRAFT' && (
          <MenuItem onClick={() => { handleEdit(selectedCampaign); handleMenuClose(); }}>
            <ScheduleIcon fontSize="small" sx={{ mr: 1 }} /> Schedule
          </MenuItem>
        )}
        {selectedCampaign?.status === 'SCHEDULED' && (
          <MenuItem onClick={handleCancelClick}>
            <CancelIcon fontSize="small" sx={{ mr: 1 }} /> Cancel Schedule
          </MenuItem>
        )}
        {(selectedCampaign?.status === 'SENT' || selectedCampaign?.status === 'SENDING') && (
          <MenuItem
            component={Link}
            to={`/${schoolSlug}/admin/custom-sms/${selectedCampaign?.id}/analytics`}
            onClick={handleMenuClose}
          >
            <AnalyticsIcon fontSize="small" sx={{ mr: 1 }} /> Analytics
          </MenuItem>
        )}
        {(selectedCampaign?.status === 'DRAFT' || selectedCampaign?.status === 'FAILED') && (
          <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete
          </MenuItem>
        )}
      </Menu>

      {/* Send Confirmation Dialog */}
      <ConfirmDialog
        open={sendDialogOpen}
        title="Send Campaign"
        message={`Are you sure you want to send "${campaignToSend?.title}" to ${campaignToSend?.totalRecipients || 0} recipients? This action cannot be undone.`}
        confirmLabel="Send"
        confirmColor="primary"
        onConfirm={handleSendConfirm}
        onCancel={() => {
          setSendDialogOpen(false);
          setCampaignToSend(null);
        }}
        loading={sendMutation.isPending}
      />

      {/* Cancel Confirmation Dialog */}
      <ConfirmDialog
        open={cancelDialogOpen}
        title="Cancel Scheduled Campaign"
        message={`Are you sure you want to cancel the scheduled sending of "${campaignToCancel?.title}"? The campaign will be moved back to draft status.`}
        confirmLabel="Cancel Schedule"
        confirmColor="warning"
        onConfirm={handleCancelConfirm}
        onCancel={() => {
          setCancelDialogOpen(false);
          setCampaignToCancel(null);
        }}
        loading={cancelMutation.isPending}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Campaign"
        message={`Are you sure you want to delete "${campaignToDelete?.title}"? This action cannot be undone.`}
        confirmLabel="Delete"
        confirmColor="error"
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          setDeleteDialogOpen(false);
          setCampaignToDelete(null);
        }}
        loading={deleteMutation.isPending}
      />
    </Box>
  );
}
